import { ReactElement, useMemo } from 'react';

import League from '../../types/League';
import GratiCard, { GratiCardSlotClickHandlers } from '../common/GratiCard';

import { getLeagueCategory, getLeagueDetails } from './leagueCardUtils';
import { LeaguePreviewElement } from './leaguePreviewTypes';

interface LeagueCardPreviewProps {
  league: League;
  variant?: 'elevation' | 'outlined' | 'none';
  /** Called when a card element is clicked (for interactive preview) */
  onElementClick?: (element: LeaguePreviewElement) => void;
}

/**
 * A preview version of LeagueCard that doesn't depend on Firestore hooks.
 * Used for previewing leagues before they are saved.
 *
 * Key differences from LeagueCard:
 * - No data hooks (useData, useNavigate, etc.)
 * - No TopActions menu (no edit button)
 * - Reactive to league prop changes for real-time preview updates
 * - Supports interactive mode via onElementClick for edit preview
 */
export default function LeagueCardPreview({
  league,
  variant = 'outlined',
  onElementClick,
}: LeagueCardPreviewProps): ReactElement {
  // Build slot click handlers for interactive preview mode
  const slotClickHandlers: GratiCardSlotClickHandlers | undefined = useMemo(() => {
    if (!onElementClick) return undefined;

    return {
      avatar: () => onElementClick('logo'),
      title: () => onElementClick('name'),
      category: () => onElementClick('orgName'),
      description: () => onElementClick('description'),
      detail: (index: number) => {
        const detailMap: LeaguePreviewElement[] = ['competitionLevel', 'playDays', 'format', 'season'];
        onElementClick(detailMap[index] ?? 'competitionLevel');
      },
    };
  }, [onElementClick]);

  return (
    <GratiCard
      key={league.itemId || 'preview'}
      variant={variant}
      avatar={league.logo}
      avatarStorageLocation={
        league.orgId && league.itemId
          ? `org/${league.orgId}/league/${league.itemId}/orgMemberLeagueAdminFile`
          : undefined
      }
      category={getLeagueCategory(league)}
      title={league.name}
      description={league.description ?? undefined}
      details={getLeagueDetails(league)}
      slotClickHandlers={slotClickHandlers}
    />
  );
}
